import type {
  AttributeReviewDraftSelection,
  AttributeReviewMappingRow,
} from "@/hooks/useAttributeReview";
import { useMapRowValues } from "@/hooks/useMapRowValues";
import type { usePoolSearch } from "@/hooks/usePoolSearch";
import type { AttributeReviewValueFixInput } from "@/graphql/generated";
import { fieldCardView } from "@/lib/attributeReview/fieldCardView";
import {
  pickDifferences,
  type PickPart,
} from "@/lib/attributeReview/shownAnswer";
import type { Mark } from "@/lib/attributeReview/stepMarks";
import type { PoolValue } from "@/lib/attributeReview/valueView";
import { formatProductType } from "@/lib/formatProductType";

import { PoolSection, type Pool } from "../AttributeReviewRow/PoolSection";
import { StoreValueChips } from "../AttributeReviewRow/StoreValueChips";
import type { useRowDecision } from "../AttributeReviewRow/useRowDecision";

import { MapValuesButton } from "./MapValuesButton";
import { ReviewStep } from "./ReviewStep";
import { StepCard } from "./StepCard";
import { StepNote } from "./StepNote";

const PART_LABEL: Record<PickPart, string> = {
  leaf: "Field name",
  kind: "Kind",
  path: "Path",
};

type Props = {
  row: AttributeReviewMappingRow;
  draft?: AttributeReviewDraftSelection;
  readOnly: boolean;
  pool: Pool | null;
  values: PoolValue[];
  fixes: Record<string, AttributeReviewValueFixInput>;
  decision: ReturnType<typeof useRowDecision>;
  poolSearch: ReturnType<typeof usePoolSearch>;
  hiddenCount: number;
  pickedPath: string | null;
  pickedLeaf: string | null;
  noField: boolean;
  sessionId: string;
  marketplace: string;
  mark: Mark;
  needLook: number;
};

export function ValuesStep(p: Props) {
  const { row, values, readOnly, pickedPath } = p;
  const { mapValues, isMapping } = useMapRowValues();
  const storeCount = Number(row.sourceEnumValueCount ?? 0);
  const status =
    p.mark === "todo"
      ? "Waits for step 1"
      : p.needLook > 0
        ? `${p.needLook} to check`
        : values.length === 0
          ? storeCount > 0
            ? "Not mapped yet"
            : "No list"
          : "All match";
  return (
    <ReviewStep n={2} mark={p.mark} title="Values" status={status}>
      <StepCard>
        {!pickedPath && !p.pickedLeaf && values.length === 0 && (
          <p className="text-[13px] text-slate-500 dark:text-slate-400">
            {p.noField
              ? "No values. The seller never sees this field."
              : "Values show once a field is picked."}
          </p>
        )}
        {!readOnly && p.hiddenCount > 0 && (
          <StepNote
            tone="warn"
            title={`${p.hiddenCount} values are mapped, but hidden.`}
            body="They were mapped for a different pick. Map them again below."
          >
            {pickDifferences(row, p.draft).map(({ part, saved, picked }) => (
              <p key={part} className="text-xs [overflow-wrap:anywhere]">
                {PART_LABEL[part]}: saved{" "}
                <code className="font-mono">{saved}</code>, your pick{" "}
                <code className="font-mono">{picked}</code>
              </p>
            ))}
          </StepNote>
        )}
        <PoolSection
          canTakePool={Boolean(p.pickedLeaf)}
          pool={p.pool}
          values={values}
          readOnly={readOnly}
          fixes={p.fixes}
          setFix={p.decision.setFix}
          undoFix={p.decision.undoFix}
          storeName={formatProductType(p.marketplace)}
          text={
            storeCount === 0
              ? {
                  picked: p.decision.pool,
                  onPick: p.decision.setPool,
                  search: p.poolSearch,
                }
              : null
          }
        />
        {values.length === 0 && storeCount > 0 && (
          <div className="space-y-2">
            <p className="text-sm text-slate-700 dark:text-slate-300">
              {formatProductType(p.marketplace)} sends {storeCount} values. None
              are mapped yet.
            </p>
            <StoreValueChips
              values={
                fieldCardView(
                  (row.marketplaceRawJson ?? {}) as Record<string, unknown>,
                ).values
              }
            />
          </div>
        )}
        {!readOnly && pickedPath && (
          <MapValuesButton
            values={values}
            storeValueCount={storeCount}
            busy={isMapping}
            onClick={() =>
              mapValues(
                p.sessionId,
                String(row.id ?? ""),
                pickedPath === row.path ? null : pickedPath,
              )
            }
          />
        )}
      </StepCard>
    </ReviewStep>
  );
}
